import { SyncErrorClassification } from '@/actions/plaid/authActions'

/** The recovery action a sync-error classification offers, if any. */
export type SyncErrorAction = 'reconnect' | 'retry' | null

export type SyncErrorMessage = {
  /** Bold headline / lead. */
  title: string
  /** Body copy. */
  message: string
  /** Which recovery control the surface should offer; each surface renders it its own way. */
  action: SyncErrorAction
  /** Word within `message` that carries the action  */
  actionWord?: string
}

const genericConnectionIssue =
  'We are experiencing some issues connecting at this time. Please retry again later.'

/**
 * Plaid sync-error copy, keyed by classification. Plain data only — each surface
 * (onboarding, self-serve settings) reads `action` and renders the retry/reconnect
 * control its own way.
 *
 * Exhaustive `Record` — a new classification won't compile until its copy is added.
 */
export const SYNC_ERROR_MESSAGES: Record<
  SyncErrorClassification,
  SyncErrorMessage
> = {
  [SyncErrorClassification.REAUTH_REQUIRED]: {
    title: 'Your bank connection has expired',
    message: 'Reconnect your account to resume syncing.',
    action: 'reconnect',
    actionWord: 'Reconnect',
  },
  [SyncErrorClassification.USER_SETUP_REQUIRED]: {
    title: 'Action required at your bank',
    message:
      'Please log in to your bank account and complete any pending setup or verification steps, then reconnect.',
    action: 'reconnect',
  },
  [SyncErrorClassification.PERMISSION_DENIED]: {
    title: 'Unable to connect',
    message: "Automatic transaction syncing isn't available for this account.",
    action: null,
  },
  [SyncErrorClassification.TRANSIENT_RETRYABLE]: {
    title: 'Unable to connect',
    message:
      'We are experiencing some issues connecting at this time. Please retry again.',
    action: 'retry',
    actionWord: 'retry',
  },
  [SyncErrorClassification.INSTITUTION_UNSUPPORTED]: {
    title: 'Unable to connect',
    message:
      "This institution isn't supported for automatic transaction syncing.",
    action: null,
  },
  [SyncErrorClassification.INTERNAL_FAILURE]: {
    title: 'Unable to sync',
    message:
      'An internal issue is preventing automatic transaction syncing for this account.',
    action: null,
  },
  [SyncErrorClassification.UNKNOWN_PLAID_ERROR]: {
    title: 'Unable to connect',
    message: genericConnectionIssue,
    action: null,
  },
}
